/**
 * Error utilities: standardized message extraction, normalization, and custom error classes.
 */

export function extractErrorMessage(error: unknown): string {
  if (!error) return 'Unknown error';
  if (error instanceof Error) return error.message || error.name || 'Error';
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export function extractStatusCode(error: unknown): number | undefined {
  // Common patterns (fetch/axios-like)
  const anyErr = error as any;
  return (
    anyErr?.status ||
    anyErr?.response?.status ||
    (typeof anyErr?.code === 'number' ? anyErr.code : undefined)
  );
}

export function normalizeError(error: unknown): NormalizedError {
  const message = extractErrorMessage(error);
  const status = extractStatusCode(error);
  if (error instanceof ApplicationError) {
    return {
      type: error.name,
      message,
      status: error.statusCode,
      isOperational: true,
      original: error,
    };
  }
  return {
    type: 'UnknownError',
    message,
    status,
    isOperational: false,
    original: error,
  };
}

export interface NormalizedError {
  type: string;
  message: string;
  status?: number;
  isOperational: boolean;
  original: unknown;
}

export function isOperationalError(error: unknown): boolean {
  return error instanceof ApplicationError;
}

// Base application error
export class ApplicationError extends Error {
  constructor(message: string, public readonly statusCode?: number) {
    super(message);
    this.name = 'ApplicationError';
  }
}

export class ValidationError extends ApplicationError {
  constructor(message: string) {
    super(message, 400);
    this.name = 'ValidationError';
  }
}

export class NotFoundError extends ApplicationError {
  constructor(message: string = 'Resource not found') {
    super(message, 404);
    this.name = 'NotFoundError';
  }
}

export class UnauthorizedError extends ApplicationError {
  constructor(message: string = 'Unauthorized') {
    super(message, 401);
    this.name = 'UnauthorizedError';
  }
}

export class ConflictError extends ApplicationError {
  constructor(message: string = 'Conflict') {
    super(message, 409);
    this.name = 'ConflictError';
  }
}

export class RateLimitError extends ApplicationError {
  constructor(message: string = 'Rate limit exceeded') {
    super(message, 429);
    this.name = 'RateLimitError';
  }
}

export class ExternalServiceError extends ApplicationError {
  constructor(message: string = 'External service error', statusCode?: number) {
    super(message, statusCode);
    this.name = 'ExternalServiceError';
  }
}
